import { inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { attemptAsync } from "es-toolkit";
import { lastValueFrom, Observable } from "rxjs";
import { DraftDocument } from "src/app/backend/models/draft-document";
import { sntLog } from "src/app/common/logger";
import { DraftDocumentRepository } from "src/app/repositories/draft-document.repository";
import { TabService } from "src/app/services/tab.service";
import { BrokerService } from "src/app/sse/broker.service";
import { SseEvents } from "src/app/sse/sse-events";
import { dateUtil } from "src/app/support/date-util";
import { sntTranslate } from "src/app/support/translate";

const logger = sntLog("DraftDocumentStoreService");

type DraftDocumentStoreServiceOpenResult<T> = {
  resource: T;
  draftDocument: DraftDocument | null;
};

type DraftDocumentStoreServiceOpenParams<T> = {
  draftDocumentId: string;
  resource: Observable<T>;
  applyFn: ({
    resource,
    draftDocument
  }: {
    draftDocument: DraftDocument;
    resource: T;
  }) => Promise<T>;
  getInitialContent?: (resource: T) => string | undefined;
};

@Injectable()
export class DraftDocumentStoreService {
  readonly #draftDocumentRepository = new DraftDocumentRepository();
  readonly #tabService = inject(TabService);
  readonly #brokerService = inject(BrokerService);

  async open<T>({
    draftDocumentId,
    resource,
    applyFn,
    getInitialContent
  }: DraftDocumentStoreServiceOpenParams<T>): Promise<
    DraftDocumentStoreServiceOpenResult<T>
  > {
    const loadedResource = await lastValueFrom(resource);

    const content: string | undefined = getInitialContent?.(loadedResource);

    let draftDocument: DraftDocument | undefined =
      await this.#draftDocumentRepository.loadById(draftDocumentId);

    if (!draftDocument) {
      return {
        resource: loadedResource,
        draftDocument: await this.#getInstance({ id: draftDocumentId, content })
      };
    }

    return {
      resource: await applyFn({ resource: loadedResource, draftDocument }),
      draftDocument
    };
  }

  async exist({
    draftDocumentId
  }: {
    draftDocumentId: string;
  }): Promise<boolean> {
    return !!(await this.load({ draftDocumentId }));
  }

  load({
    draftDocumentId
  }: {
    draftDocumentId: string;
  }): Promise<DraftDocument | undefined> {
    return this.#draftDocumentRepository.loadById(draftDocumentId);
  }

  async save(draftDocument: DraftDocument): Promise<void> {
    if (!draftDocument.created) {
      draftDocument.created = true;

      const [error, createdDraftDocument] = await attemptAsync(() =>
        this.#create(draftDocument)
      );

      if (error) {
        logger.exception(error);
        draftDocument.created = false;
      }
    }

    draftDocument.updatedAt = dateUtil().toISOString();
    await this.#update(draftDocument);

    this.#tabService.blinkText({ text: sntTranslate(marker("Bozza salvata")) });
  }

  async commit({
    draftDocument,
    commitFn
  }: {
    draftDocument: DraftDocument | null;
    commitFn: () => Promise<void>;
  }): Promise<void> {
    await commitFn();

    if (draftDocument) {
      await this.#delete(draftDocument);
    }
  }

  async #getInstance({
    id,
    content
  }: {
    id: string;
    content: string | undefined;
  }): Promise<DraftDocument | null> {
    return <DraftDocument>{
      id,
      name: "",
      content,
      createdAt: dateUtil().toISOString(),
      updatedAt: undefined,
      created: false
    };
  }

  async #create(draftDocument: DraftDocument): Promise<void> {
    await this.#draftDocumentRepository.create(draftDocument);
    this.#brokerService.publish({
      event: SseEvents.DIAGNOSTIC_DRAFT_DOCUMENT_CREATED,
      data: JSON.stringify(draftDocument),
      isOptmistic: false
    });
  }

  async #update(draftDocument: DraftDocument): Promise<void> {
    await this.#draftDocumentRepository.update(draftDocument);
    this.#brokerService.publish({
      event: SseEvents.DIAGNOSTIC_DRAFT_DOCUMENT_UPDATED,
      data: JSON.stringify(draftDocument),
      isOptmistic: false
    });
  }

  async #delete(draftDocument: DraftDocument): Promise<void> {
    await this.#draftDocumentRepository.delete(draftDocument.id);
    this.#brokerService.publish({
      event: SseEvents.DIAGNOSTIC_DRAFT_DOCUMENT_DELETED,
      data: JSON.stringify(draftDocument),
      isOptmistic: false
    });
  }
}
